/* eslint-disable consistent-return */
/* eslint-disable react/button-has-type */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-len */
import './styles.css';

import { getOneCase } from '@brands/services/cases/getCaseById';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Moment from 'moment';
import { Editor } from 'primereact/editor';
import React, { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { RiDeleteBinLine } from 'react-icons/ri';

import CareAdvisorProfileCardModal from '../../../Dashboard/ProfileCards/CareAdvisor/ProfileCards';
import PatientProfileCardModal from '../../../Dashboard/ProfileCards/Patient/ProfileCards';
import ProviderProfileCardModal from '../../../Dashboard/ProfileCards/Provider/ProfileCards';
import { IGMConversationParticipant } from '../../../services/communication/types/IGMConversationParticipant';
import { IGMMessage } from '../../../services/communication/types/IGMMessage';
import { UserProfile, UserRoleName } from '../../../services/identity/types/UserProfile';
import { shouldShowDoctorTitle } from '../../../Utils/getProviderTitle';
import styles from './case-styles.module.scss';
import MessageContent from './MessageContent';

interface IActiveChatBubbleProps {
  direction: 'incoming' | 'outgoing';
  messageDetails: {
    id: string;
  };
  conversationState: 'active' | 'inactive' | 'closed' | undefined;
  participants: IGMConversationParticipant[] | null | undefined;
  providerCredentials?: string | null;
  message: IGMMessage;
  isVCRoom?: boolean;
  editMessage: (message: IGMMessage, body: string) => Promise<void>;
  deleteMessage: (message: IGMMessage) => Promise<void>;
  messageIndex: number;
  handleMediaLoad: (index: number) => void;
  listInnerRef: React.RefObject<HTMLDivElement>;
  messageCreator: IGMConversationParticipant | UserProfile | undefined | null;
  scrollTothis: (number: number, index: number) => void;
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  isInEditMode: string;
  setIsInEditMode: React.Dispatch<React.SetStateAction<string>>;
  currentCaseId: number | undefined;
  isLastMessage: boolean;
  hoveredMessageId: string | null;
  setHoveredMessageId: React.Dispatch<React.SetStateAction<string | null>>;
}

const CaseActiveChatBubble: React.FC<IActiveChatBubbleProps> = ({
  conversationState,
  direction,
  isVCRoom,
  messageDetails,
  editMessage,
  deleteMessage,
  message,
  participants,
  providerCredentials,
  messageIndex,
  handleMediaLoad,
  messageCreator,
  listInnerRef,
  scrollTothis,
  setEditMode,
  isInEditMode,
  setIsInEditMode,
  currentCaseId,
  isLastMessage,
  hoveredMessageId,
  setHoveredMessageId,
}) => {
  const [profileCardModal, setProfileCardModal] = useState(false);
  const [providerProfileCardModal, setProviderProfileCardModal] = useState(false);
  const [caProfileCardModal, setCAProfileCardModal] = useState(false);
  const [participant, setParticipant] = useState<UserProfile | null>(null);
  const [patientCurrentState, setPatientCurrentState] = useState('');
  const absoluteRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<any>(null);
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const isHovered = hoveredMessageId === messageDetails.id;
  const { itemStyle, divStyle, timeContainer } =
    direction === 'incoming'
      ? {
          itemStyle: isVCRoom ? styles.vc_received_msg : styles.received_msg,
          divStyle: isVCRoom ? styles.vc_received_withd_msg : styles.received_withd_msg,
          timeContainer: styles.time_container_received,
        }
      : {
          itemStyle: isVCRoom ? styles.vc_outgoing_msg : styles.outgoing_msg,
          divStyle: isVCRoom ? styles.vc_sent_msg : styles.sent_msg,
          timeContainer: styles.time_container_outgoing,
        };

  const [newMessage, setNewMessage] = useState(message.body || '');

  const handleEditButtonClick = (): void => {
    setHoveredMessageId(null);
    setIsInEditMode(messageDetails.id);
    setNewMessage(message.body || '');
  };

  const onMessageChanged = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setNewMessage(event.target.value);
  };

  const [avatarIcon, setAvatarIcon] = useState(false);
  const handleAvatarError = (): void => {
    setAvatarIcon(true);
  };

  const [containerHeight, setContainerHeight] = useState(0);
  const chatMessageContainerRef = useRef<HTMLDivElement>(null);

  const displayParticipantProfile = async (
    e: React.MouseEvent<HTMLDivElement | HTMLSpanElement, MouseEvent>,
  ): Promise<void> => {
    e.stopPropagation();
    e.preventDefault();
    const currentRelatedCase = await getOneCase(currentCaseId || 0);
    if (currentRelatedCase) setPatientCurrentState(currentRelatedCase.patient_current_state || '');
    const messageCreatorRole =
      typeof messageCreator?.role === 'string' ? messageCreator.role : messageCreator?.role?.name || '';

    if (messageCreatorRole === UserRoleName.Patient || messageCreatorRole === UserRoleName.Dependent) {
      setParticipant(currentRelatedCase.patient || null);
      setProfileCardModal(true);
    } else if (messageCreatorRole === UserRoleName.Provider) {
      setParticipant(currentRelatedCase.provider || null);
      setProviderProfileCardModal(true);
    } else if (messageCreatorRole === UserRoleName.CareAdvisor) {
      setParticipant(currentRelatedCase.care_advisor || null);
      setCAProfileCardModal(true);
    }
  };

  const scrollIntoMessage = useCallback((): void => {
    const elem = document?.getElementById(messageDetails.id);
    elem?.scrollIntoView({ behavior: 'smooth' });
  }, [messageDetails]);

  useEffect(() => {
    if (chatMessageContainerRef.current) {
      const newHeight = chatMessageContainerRef.current.clientHeight;
      if (newHeight !== containerHeight) {
        setContainerHeight(newHeight);
        scrollIntoMessage();
        chatMessageContainerRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [containerHeight, chatMessageContainerRef, scrollIntoMessage]);

  useEffect(() => {
    const quill = editorRef.current?.getQuill();
    const editorEl = quill?.root;

    if (!quill || !editorEl) {
      console.warn('Quill or editor element not found');
      return;
    }

    // Clear all existing Enter key bindings
    quill.keyboard.bindings.Enter = [];
    quill.keyboard.bindings[13] = [];

    // Enter = Trigger Save button
    quill.keyboard.addBinding(
      {
        key: 'Enter',
        shiftKey: false,
      },
      (range: any, context: any) => {
        if (newMessage.trim() && isInEditMode === messageDetails.id) {
          context.event.preventDefault();
          context.event.stopPropagation();
          saveButtonRef.current?.click();
          return false;
        }
        return true;
      },
    );

    // Shift+Enter = Insert newline
    quill.keyboard.addBinding(
      {
        key: 'Enter',
        shiftKey: true,
      },
      (range: any) => {
        quill.insertText(range.index, '\n', 'user');
        quill.setSelection(range.index + 1, 0, 'user');
        return true;
      },
    );

    // Low-level keydown listener as fallback
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Enter' && !e.shiftKey && isInEditMode === messageDetails.id && newMessage.trim()) {
        e.preventDefault();
        e.stopPropagation();
        saveButtonRef.current?.click();
      }
    };

    editorEl.addEventListener('keydown', handleKeyDown);

    return () => {
      editorEl.removeEventListener('keydown', handleKeyDown);
      quill.keyboard.bindings.Enter = [];
      quill.keyboard.bindings[13] = [];
    };
  }, [isInEditMode, messageDetails.id, newMessage]);

  const renderHeader = (): JSX.Element => {
    return (
      <span className="ql-formats">
        <button className="ql-bold" aria-label="Bold" />
        <button className="ql-italic" aria-label="Italic" />
        <button className="ql-underline" aria-label="Underline" />
        <button className="ql-list" value="ordered" aria-label="Numbered List" />
        <button className="ql-list" value="bullet" aria-label="Bulleted List" />
      </span>
    );
  };

  const showDropDown =
    direction === 'outgoing' &&
    (conversationState === 'active' || conversationState === 'inactive') &&
    message.body !== null &&
    !isVCRoom &&
    message.attributes.archived_at === undefined;

  const ref = useDetectClickOutside({
    onTriggered: () => {
      if (isHovered) {
        setHoveredMessageId(null);
      }
    },
  });

  const header = renderHeader();

  return (
    <div
      style={{
        padding: '0 15px 0px 15px',
      }}
      className={`chatBubbleDiv ${isInEditMode === messageDetails.id ? styles.isInEditMode : ''} ${
        styles.chatBubbleDiv
      }`}
    >
      <li className={itemStyle} key={messageDetails.id} style={{ position: 'relative' }}>
        <div ref={ref} className={`${divStyle} ${styles.divStyle}`}>
          <section
            className={styles.dateContainer}
            style={{ justifyContent: direction === 'incoming' ? 'flex-start' : 'flex-end' }}
          >
            <span className={`fs-exclude ${timeContainer}`}>
              <span style={{ display: direction === 'incoming' ? 'flex' : 'none' }}>
                <span
                  className={`${styles.authorName}`}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => displayParticipantProfile(e)}
                >
                  {participants?.find((p) => +p.identity === +message.created_by!)?.role === 'Provider' &&
                    shouldShowDoctorTitle(providerCredentials) && (
                      <span className={styles.providerTag}>Dr. </span>
                    )}
                  {participants?.find((p) => +p.identity === +message.created_by!)?.full_name}
                  {participants?.find((participant) => +participant.identity === +message.created_by!)?.status ===
                    'Deactivated' && '(Deactivated)'}
                  <span className={styles.hyphen}> </span>
                </span>
              </span>
              <span className={!isVCRoom ? styles.time_date : styles.vc_time_date}>
                {Moment(message.created_at.endsWith('Z') ? message.created_at : `${message.created_at}Z`)
                  .local()
                  .format('MM/DD/YY hh:mma')}
              </span>
              {message.attributes.archived_at === undefined &&
              message.body !== null &&
              message.updated_at !== null &&
              message.updated_at !== message.created_at ? (
                <span className={styles.time_date}>
                  This message was edited.(
                  {Moment(message.created_at.endsWith('Z') ? message.created_at : `${message.created_at}Z`)
                    .local()
                    .format('MM/DD/YY hh:mma')}
                  )
                </span>
              ) : (
                ''
              )}
            </span>
          </section>
          <div className={styles.messageWrapper}>
            {isInEditMode === messageDetails.id ? (
              <div>
                <Editor
                  ref={editorRef}
                  className={`fs-exclude ${styles.editBox}`}
                  value={newMessage}
                  onTextChange={(e) => setNewMessage(e.htmlValue || '')}
                  style={{ height: '320px' }}
                  headerTemplate={header}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && newMessage.trim()) {
                      e.preventDefault();
                      e.stopPropagation();
                      saveButtonRef.current?.click();
                    }
                  }}
                />
                <div className={styles.edit_container}>
                  <button
                    type="button"
                    ref={saveButtonRef}
                    onClick={() => {
                      const quillInstance = editorRef.current?.getQuill();
                      const plainText = quillInstance?.getText().trim();

                      let html = newMessage.trim();

                      // Remove empty paragraphs and empty list items at the end
                      html = html.replace(/(<(p|li)>(\s|<br>)*<\/\2>\s*)+$/g, '');

                      // Optionally remove empty <ul> or <ol> if they're completely empty
                      html = html.replace(/<(ul|ol)>\s*<\/\1>/g, '');

                      if (plainText?.length) {
                        editMessage(message, html);
                        setIsInEditMode('');
                        setEditMode(false);
                        setHoveredMessageId(null);
                      }
                    }}
                    className={styles.submit_buttton}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsInEditMode('');
                      setHoveredMessageId(null);
                    }}
                    className={styles.cancel_buttton}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <MessageContent
                  messageIndex={messageIndex}
                  handleMediaLoad={handleMediaLoad}
                  message={message}
                  mediaIDs={message.media_ids}
                  direction={direction}
                />
                {direction === 'outgoing' &&
                (conversationState === 'active' || conversationState === 'inactive') &&
                message.body !== null &&
                isHovered ? (
                  <div
                    className={`fs-unmask ${styles.dropDown}`}
                    ref={absoluteRef}
                    style={isLastMessage ? { top: 0 } : undefined}
                  >
                    <div onClick={handleEditButtonClick}>
                      <EditOutlinedIcon className={styles.dropDownIcon} />
                      Edit
                    </div>
                    <div className={styles.deleteBtn} onClick={() => deleteMessage(message)} style={{ color: 'red' }}>
                      <RiDeleteBinLine className={styles.dropDownIcon} title="Delete Message" />
                      Delete
                    </div>
                  </div>
                ) : (
                  ''
                )}
              </>
            )}
          </div>
        </div>
        <span
          ref={chatMessageContainerRef}
          id={messageDetails.id}
          style={{ position: 'absolute', bottom: '0', right: '0' }}
        />
      </li>
      {profileCardModal && (
        <PatientProfileCardModal
          patientIdentity={participant}
          isOpen={profileCardModal}
          toggleModal={setProfileCardModal}
        />
      )}
      {providerProfileCardModal && (
        <ProviderProfileCardModal
          isOpen={providerProfileCardModal}
          toggleModal={setProviderProfileCardModal}
          providerIdentity={participant}
          patientCurrentStateValue={patientCurrentState}
        />
      )}
      {caProfileCardModal && (
        <CareAdvisorProfileCardModal
          isOpen={caProfileCardModal}
          toggleModal={setCAProfileCardModal}
          careAdvisorIdentity={participant}
        />
      )}
    </div>
  );
};

CaseActiveChatBubble.defaultProps = {
  isVCRoom: false,
};

export default CaseActiveChatBubble;
